<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed } from "vue"
import { useI18n } from "vue-i18n"

// ================================
// TYPES & INTERFACES
// ================================
//
// DEV-83 spike (parent DEV-77) — banner affiché dans SensorInfoDisplay
// quand `informations.iot.etat === "inexistant"`.
//
// Le banner ne contient AUCUNE logique de recherche réseau : il émet juste
// `edit-sensor` que le parent capte pour ouvrir le SourceAppendDialog en mode
// édition (workflow déjà éprouvé via `sensor-form.machine.ts` qui supporte
// nativement le mode édition + l'event MODIFY_OPERATOR pour relancer la
// recherche backend ou changer d'opérateur).
//
// Le bouton "Modifier le capteur" couvre les 3 cas user :
// 1. Faute de frappe sur l'identifiant
// 2. DEVEUI ré-enregistré chez l'opérateur entre temps (relancer la recherche)
// 3. Capteur migré chez un autre opérateur (saisie manuelle dans le form)
//
type Props = {
    /**
     * Nom de l'opérateur chez qui le capteur n'est pas référencé.
     * Affiché dans le message d'erreur principal.
     */
    operator: string
    /**
     * Nom du capteur (utilisé dans le wording du dialog de suppression).
     */
    sensorName?: string
}

// ================================
// PROPS / EMITS
// ================================
withDefaults(defineProps<Props>(), {
    sensorName: "",
})

const emit = defineEmits<{
    /**
     * Demande d'ouverture du SourceAppendDialog en mode édition.
     * Le parent doit câbler ça pour ouvrir le dialog avec le sourceId du capteur.
     */
    (e: "edit-sensor"): void
    /**
     * Demande de suppression du capteur.
     * Le parent peut soit gérer lui-même, soit déléguer à SourceDeleteDialog.
     */
    (e: "delete-sensor"): void
}>()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            tag: {
                non_existant: "Inexistant",
            },
            tooltip: {
                non_existant:
                    "Le capteur n'a pas été trouvé sur le réseau de l'opérateur",
            },
            error: {
                message:
                    "{operator} nous remonte qu'aucun capteur n'est référencé avec cet identifiant sur leur réseau.",
                button: "Modifier les informations",
            },
            menu: {
                delete: "Supprimer le capteur",
            },
            dialog: {
                delete: {
                    title: "Supprimer ce capteur ?",
                    message:
                        "La suppression du capteur entraîne la perte de l'historique de données associé. Cette action est irréversible.",
                    confirm: "Supprimer définitivement",
                    cancel: "Annuler",
                },
            },
        },
        en: {
            tag: {
                non_existant: "Not found",
            },
            tooltip: {
                non_existant:
                    "The sensor was not found on the operator's network",
            },
            error: {
                message:
                    "{operator} reports that no sensor is registered with this identifier on their network.",
                button: "Edit information",
            },
            menu: {
                delete: "Delete sensor",
            },
            dialog: {
                delete: {
                    title: "Delete this sensor?",
                    message:
                        "Deleting this sensor will permanently remove its data history. This action cannot be undone.",
                    confirm: "Delete permanently",
                    cancel: "Cancel",
                },
            },
        },
    },
})

// ================================
// LOCAL STATE
// ================================

/**
 * Visibilité du dialog de confirmation de suppression.
 */
const showDeleteDialog = ref(false)

/**
 * Items du menu contextuel destructif (suppression).
 */
const menuItems = computed(() => [
    {
        label: t("menu.delete"),
        icon: "pi pi-trash",
        command: () => {
            showDeleteDialog.value = true
        },
    },
])

// ================================
// EVENT HANDLERS
// ================================

/**
 * Demande au parent d'ouvrir SourceAppendDialog en mode édition.
 */
function onEditSensor() {
    emit("edit-sensor")
}

/**
 * Confirmation de suppression : émet event au parent qui gère la suppression.
 */
function onConfirmDelete() {
    showDeleteDialog.value = false
    emit("delete-sensor")
}

/**
 * Référence au menu PrimeVue pour pouvoir l'ouvrir au clic du bouton.
 */
const menuRef = ref()
function onMenuToggle(event: Event) {
    menuRef.value?.toggle(event)
}
</script>

<template>
    <!--
        DEV-83 spike (parent DEV-77) — banner "capteur inexistant".

        Le badge "Inexistant" déménage dans la section Identifiant de
        SensorInfoDisplay (à côté de la valeur). Le banner ici se concentre sur
        le message d'erreur + les actions (modifier / supprimer).

        Pas de modal interne : l'action "Modifier le capteur" délègue au parent
        qui ouvre le SourceAppendDialog en mode édition (workflow déjà éprouvé
        via sensor-form.machine.ts).
    -->
    <div>
        <!-- Message d'erreur principal + CTA + menu suppression -->
        <BaseMessage
            severity="error"
            :closable="false"
            id="sensor-inexistant-message"
            role="status"
            aria-live="polite"
        >
            <div class="flex flex-col gap-3">
                <p class="m-0">
                    {{ t("error.message", { operator }) }}
                </p>
                <div class="flex items-center gap-2">
                    <BaseButton
                        :label="t('error.button')"
                        icon="pi pi-pencil"
                        size="small"
                        severity="danger"
                        outlined
                        aria-describedby="sensor-inexistant-message"
                        @click="onEditSensor"
                    />
                    <BaseButton
                        text
                        rounded
                        size="small"
                        severity="danger"
                        icon="pi pi-ellipsis-v"
                        aria-label="Actions sur le capteur"
                        aria-haspopup="true"
                        @click="onMenuToggle"
                    />
                    <BaseMenu ref="menuRef" :model="menuItems" :popup="true" />
                </div>
            </div>
        </BaseMessage>

        <!-- Confirm dialog suppression (réutilisé tel quel — workflow standard) -->
        <BaseDialog
            v-model:visible="showDeleteDialog"
            :header="t('dialog.delete.title')"
            modal
            :draggable="false"
            :style="{ width: '32rem' }"
        >
            <p class="m-0">
                {{ t("dialog.delete.message") }}
            </p>
            <template #footer>
                <BaseButton
                    :label="t('dialog.delete.cancel')"
                    text
                    autofocus
                    @click="showDeleteDialog = false"
                />
                <BaseButton
                    :label="t('dialog.delete.confirm')"
                    severity="danger"
                    icon="pi pi-trash"
                    @click="onConfirmDelete"
                />
            </template>
        </BaseDialog>
    </div>
</template>
